import { Select, Typography } from '@components/base';
import { forwardRef, Ref } from 'react';
import { GroupBase, mergeStyles, Props, SelectInstance } from 'react-select';
import './index.style.scss';

interface SelectLabelProps extends Props {
  label?: string;
  helperText?: string;
  error?: boolean;
  tagLimit?: number;
  rootClassName?: string;
  value?: any;
  styles?: any;
  labelJSX?: JSX.Element;
  dropdownDirection?: 'top' | 'bottom' | 'auto';
  hideBorder?: boolean;
  disableIsFocused?: boolean;
}

const SelectLabel = forwardRef(function SelectLabel<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(props: SelectLabelProps, ref: Ref<SelectInstance<Option, IsMulti, Group>>) {
  const {
    label,
    helperText,
    error,
    required,
    className,
    tagLimit = 2,
    rootClassName,
    value,
    styles,
    labelJSX,
    dropdownDirection = 'auto',
    hideBorder = false,
    disableIsFocused = false,
    ...otherProps
  } = props;

  const borderlessStyles = mergeStyles(
    {
      control: (base: any, state: any) => ({
        ...base,
        border: 'none !important',
        boxShadow: 'none !important',
        '&:hover': {
          border: 'none !important',
          boxShadow: 'none !important'
        },
        '&:focus': { border: 'none !important', boxShadow: 'none !important' },
        '&:active': { border: 'none !important', boxShadow: 'none !important' },
        ...(state.menuIsOpen && {
          border: 'none !important',
          boxShadow: 'none !important'
        })
      })
    },
    styles || {}
  );

  const computedStyles = hideBorder ? mergeStyles(borderlessStyles, styles || {}) : styles;

  const errorClass = error ? `select-control-error ${className}` : className;
  const helperTextClass = error ? 'helper-text-error' : '';
  const showAsterisk = required ? ' *' : '';
  return (
    <label className={`select-label-wrapper ${rootClassName}`}>
      {label && (
        <div className="select-label-container">
          <span className="select-label" data-value={showAsterisk}>
            {label}
          </span>
          {labelJSX}
        </div>
      )}
      <Select
        {...otherProps}
        tagLimit={tagLimit}
        className={errorClass}
        ref={ref as any}
        required={required}
        value={value}
        styles={computedStyles}
        dropdownDirection={dropdownDirection}
        disableIsFocused={disableIsFocused}
      />
      {helperText && (
        <span className={`select-field-helperText ${helperTextClass}`}>{helperText}</span>
      )}
    </label>
  );
});

export default SelectLabel;
export type { SelectLabelProps };
